"use client";

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useApp } from "./AppContext";
import { useI18n } from "@/i18n/I18nProvider";
import { GuestIcon, PlusIcon, SearchIcon, XIcon } from "./Icons";
import type { Guest } from "@/lib/types";

export type PickedGuest = Guest & { booking_count: number; last_booking_date: string | null };

interface GuestPickerDialogProps {
  onPick: (guest: PickedGuest) => void;
  /** "Register as a new guest", carrying what was typed so the new-guest form can start from it. */
  onCreateNew: (typed: string) => void;
  onClose: () => void;
}

/**
 * Find a registered guest for a booking: shows the most frequent guests straight away, then searches by name,
 * phone number or ID card (KTP / passport) number as the user types.
 */
export function GuestPickerDialog({ onPick, onCreateNew, onClose }: GuestPickerDialogProps) {
  const { fetchWithTenant } = useApp();
  const { t, formatDate } = useI18n();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<PickedGuest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Search as the user types, waiting for a short pause so each keystroke doesn't hit the server.
  useEffect(() => {
    let cancelled = false;
    const q = query.trim();
    const timer = window.setTimeout(
      () => {
        setLoading(true);
        setError(null);
        fetchWithTenant(`/api/guests/lookup${q ? `?q=${encodeURIComponent(q)}` : ""}`)
          .then((r) => r.json())
          .then((json) => {
            if (cancelled) return;
            if (json.success) setResults(json.data);
            else setError(json.error || t("bookings.guestPicker.error"));
          })
          .catch(() => {
            if (!cancelled) setError(t("bookings.guestPicker.error"));
          })
          .finally(() => {
            if (!cancelled) setLoading(false);
          });
      },
      q ? 250 : 0,
    );
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, fetchWithTenant, t]);

  const searching = query.trim().length > 0;

  return createPortal(
    <div className="modal-backdrop guest-picker-backdrop" onClick={onClose}>
      <div className="modal-dialog" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "520px" }} role="dialog" aria-modal="true" aria-labelledby="guest-picker-title">
        <div className="modal-header">
          <div className="modal-title" id="guest-picker-title">{t("bookings.guestPicker.title")}</div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label={t("bookings.guestPicker.close")}>
            <XIcon size={18} />
          </button>
        </div>

        <div className="guest-picker-search">
          <div className="quick-find-input-wrap">
            <SearchIcon size={18} />
            <input
              ref={inputRef}
              type="search"
              className="form-control"
              placeholder={t("bookings.guestPicker.placeholder")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              enterKeyHint="search"
              autoComplete="off"
            />
          </div>
          <div className="guest-picker-hint">{t("bookings.guestPicker.hint")}</div>
        </div>

        <div className="modal-body guest-picker-body">
          <div className="guest-picker-section-label">
            {searching ? t("bookings.guestPicker.results") : t("bookings.guestPicker.frequent")}
          </div>

          {error && <div className="error-banner">{error}</div>}

          {!error && loading && results.length === 0 && <div className="guest-picker-empty">{t("bookings.guestPicker.loading")}</div>}

          {!error && !loading && results.length === 0 && (
            <div className="guest-picker-empty">{searching ? t("bookings.guestPicker.noMatch") : t("bookings.guestPicker.noFrequent")}</div>
          )}

          <div className="guest-picker-list" aria-busy={loading}>
            {results.map((g) => (
              <button key={g.id} type="button" className="guest-picker-item" onClick={() => onPick(g)}>
                <span className="guest-picker-avatar">
                  <GuestIcon size={18} />
                </span>
                <span className="guest-picker-main">
                  <span className="guest-picker-name">{g.name}</span>
                  <span className="guest-picker-meta">
                    {[
                      g.phone,
                      g.id_number_last4 ? t("bookings.guestPicker.idMasked", { last4: g.id_number_last4 }) : null,
                    ]
                      .filter(Boolean)
                      .join(" · ") || t("bookings.guestPicker.noContact")}
                  </span>
                  <span className="guest-picker-meta">
                    {g.booking_count > 0
                      ? t("bookings.guestPicker.stays", { count: g.booking_count, date: g.last_booking_date ? formatDate(g.last_booking_date) : "-" })
                      : t("bookings.guestPicker.noStays")}
                  </span>
                </span>
                <span className="guest-picker-choose">{t("bookings.guestPicker.choose")}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t("bookings.guestPicker.cancel")}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => onCreateNew(query.trim())}>
            <PlusIcon size={14} />
            <span>{t("bookings.guestPicker.createNew")}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
